import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Typography, Space, Progress, Spin, Modal, Form, Input, Radio, message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { 
  FireOutlined,
  CrownOutlined,
  TrophyOutlined,
  PlayCircleOutlined,
  UserSwitchOutlined
} from '@ant-design/icons';
import { useResponsive } from '../hooks/useResponsive';
import { useSecurityProtection } from '../hooks/useSecurityProtection';
import { useUserProfile, useExamAvailability } from '../hooks/user';
import { useCourseDetails } from '../hooks/course';
import { useClassFeedback, useStudentCheck, useFeedbackQuestions, submitFeedbackResponse, useUserSubmittedFeedback } from '../hooks/classFeedback';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import FacebookLinkModal from '../components/auth/FacebookLinkModal';

const { Title, Text } = Typography;



// Premium Unlock Component
const PremiumUnlock: React.FC = () => {
  const { isMobile } = useResponsive();
  
  return (
    <Card 
      style={{ 
        borderRadius: '16px',
        border: 'none',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}
    >
      <div style={{ padding: isMobile ? '16px' : '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: isMobile ? '8px' : '12px' }}>
          <div style={{ 
            fontSize: isMobile ? '20px' : '24px',
            marginRight: isMobile ? '8px' : '12px'
          }}>
            🎨
          </div>
          <div>
            <Title level={4} style={{ color: 'white', margin: 0, fontSize: isMobile ? '16px' : '18px' }}>
              Unlock all learning with Premium
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: isMobile ? '12px' : '14px' }}>
              to get smarter, faster
            </Text>
          </div>
        </div>
        
        <Button 
          size="large"
          style={{
            width: '100%',
            height: isMobile ? '40px' : '48px',
            borderRadius: isMobile ? '20px' : '24px',
            background: 'linear-gradient(90deg, #ffd89b 0%, #19547b 100%)',
            border: 'none',
            color: 'white',
            fontWeight: '600',
            fontSize: isMobile ? '14px' : '16px'
          }}
        >
          Explore Premium
        </Button>
      </div>
    </Card>
  );
};

// Achievement Component
const Achievement: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  // const { data: examData, isLoading: examLoading, error: examError } = useExamAvailability();
  
  const examLoading = false;
  
  if (examLoading) {
    return (
      <Card 
        style={{ 
          borderRadius: '16px',
          border: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          minHeight: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Spin size="large" />
      </Card>
    );
  }
  
  
  // Use exam data or fallback to default
  const examTitle =  'Exam Section';
  const examDescription =  'Description of the exam';
  const totalMarks = 100;
  const passMarks = 70;
  const duration = 60;
  const isActive = true;
  const examId = '1';
  
  const handleStartExam = () => {
    navigate(`/exams`, { 
      state: { 
        examId,
        examTitle,
        examDescription,
        totalMarks,
        passMarks,
        duration,
        isActive
      } 
    });
  };
  
  return (
    <Card 
      style={{ 
        borderRadius: '16px',
        border: 'none',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}
    >
      <div style={{ textAlign: 'center', padding: isMobile ? '16px' : '20px' }}>
        <div style={{
          width: isMobile ? '60px' : '80px',
          height: isMobile ? '60px' : '80px',
          borderRadius: '50%',
          background: isActive 
            ? 'linear-gradient(135deg, #2c479e 0%, #182867 100%)'
            : 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: isMobile ? '0 auto 12px' : '0 auto 16px',
          fontSize: isMobile ? '32px' : '40px'
        }}>
          {isActive ? '📝' : '🥉'}
        </div>
        
        <Title level={4} style={{ margin: '0 0 8px 0', fontSize: isMobile ? '16px' : '18px' }}>
          {examTitle}
        </Title>
        
        {/* <Text style={{ 
          color: '#595959',
          fontSize: isMobile ? '12px' : '14px',
          lineHeight: '1.5',
          display: 'block',
          marginBottom: isMobile ? '8px' : '12px'
        }}>
          {examDescription}
        </Text> */}
        
        {isActive && (
          <div style={{ 
            marginBottom: isMobile ? '12px' : '16px',
            fontSize: isMobile ? '11px' : '12px',
            color: '#8c8c8c'
          }}>
            <div>Total: {totalMarks} points • Pass: {passMarks} points</div>
            <div>Duration: {duration} minutes</div>
          </div>
        )}
        
        <Button 
          type={isActive ? "primary" : "default"}
          size="large"
          onClick={isActive ? handleStartExam : undefined}
          style={{
            width: '100%',
            height: isMobile ? '40px' : '48px',
            borderRadius: '12px',
            marginTop: isMobile ? '8px' : '12px',
            fontWeight: '500',
            fontSize: isMobile ? '14px' : '16px',
            backgroundColor: isActive ? '#2c479e' : undefined,
            borderColor: isActive ? '#2c479e' : undefined
          }}
        >
          {isActive ? 'Start Exam' : 'Continue'}
        </Button>
      </div>
    </Card>
  );
};

// Number Theory Course Component
const NumberTheoryCourse: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { data: userProfile, isLoading: userLoading } = useUserProfile();
  
  // Get the first enrolled course ID from user profile
  const courseId = userProfile?.course || '1';
  const { data: courseDetails, isLoading: courseLoading } = useCourseDetails(undefined, courseId);
  
  const handleStartCourse = () => {
    navigate(`/course/${courseId}/learn`);
  };
  
  if (userLoading || courseLoading) {
    return (
      <Card 
        style={{ 
          borderRadius: '32px',
          border: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          minHeight: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Spin size="large" />
      </Card>
    );
  }
  
  // Use course details or fallback to default values
  const courseTitle = courseDetails?.title || 'Number Theory';
  const courseLevel = courseDetails?.difficulty || 'beginner';
  const courseDuration = courseDetails?.duration || 6;
  const courseProgress = courseDetails?.progress || 0;
  
  return (
    <Card 
      style={{ 
        borderRadius: '32px',
        border: 'none',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}
    >
      <div style={{ padding: isMobile ? '16px' : '24px' }}>
        <div style={{ marginBottom: isMobile ? '16px' : '20px' }}>
          <Title level={3} style={{ margin: '0 0 4px 0', fontSize: isMobile ? '18px' : '24px' }}>
            {courseTitle}
          </Title>
        </div>
        
        {/* Course Progress or Number Grid */}
        <div style={{ 
          marginBottom: isMobile ? '16px' : '20px',
          display: 'flex',
          justifyContent: 'center'
        }}>
          {/* {courseProgress > 0 ? (
            <div style={{ width: '100%' }}>
              <Progress 
                percent={courseProgress} 
                strokeColor={{
                  '0%': '#4096ff',
                  '100%': '#1890ff',
                }}
                style={{ marginBottom: '12px' }}
              />
              <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>
                {courseProgress}% Complete
              </Text>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '4px',
              width: 'fit-content',
              padding: '12px',
              background: '#4096ff',
              borderRadius: '8px'
            }}>
              {[
                [4, 9, 2, 14],
                [12, 7, 15, 8],
                [5, 13, 6, 11],
                [1, 6, 3, 10]
              ].map((row, rowIndex) => 
                row.map((num, colIndex) => (
                  <div 
                    key={`${rowIndex}-${colIndex}`}
                    style={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: (rowIndex === 1 && colIndex === 1) ? '#faad14' : 
                                     (rowIndex === 2 && colIndex === 2) ? '#faad14' : '#1890ff',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    {num}
                  </div>
                ))
              )}
            </div>
          )} */}
          <img 
            src={courseDetails?.thumbnail_url || 'default-image.png'} 
            alt={courseTitle} 
            style={{ 
              width: '100%', 
              height: isMobile ? '120px' : 'auto',
              borderRadius: '12px',
              objectFit: 'cover'
            }} 
          />
        </div>
        {/* <div>
          <Text strong>Instructor:</Text> {courseDetails?.instructor || 'Unknown'}
        </div> */}
        <div style={{ marginBottom: isMobile ? '8px' : '10px' }}>
          <Text style={{ color: '#8c8c8c', fontSize: isMobile ? '12px' : '14px' }}>
            {courseDetails?.description || 'Divisibility Shortcuts'}
          </Text>
        </div>
        <div style={{ marginBottom: isMobile ? '16px' : '20px' }}>
          <b>
            <Text style={{ color: '#8c8c8c', fontSize: isMobile ? '11px' : '14px' }}>
          Duration  : {courseDetails?.duration_weeks}  weeks
            </Text>
          </b>
          
        </div>
        
        <Button 
          type="primary"
          size="large"
          icon={<PlayCircleOutlined />}
          onClick={handleStartCourse}
          style={{
            width: '100%',
            height: isMobile ? '44px' : '52px',
            borderRadius: '12px',
            fontSize: isMobile ? '14px' : '16px',
            fontWeight: '600',
            backgroundColor : '#182867'
          }}
        >
          {courseProgress > 0 ? 'Continue' : 'Start'}
        </Button>
      </div>
    </Card>
  );
};

// Course Topics Component
const CourseTopics: React.FC = () => {
  const { isMobile } = useResponsive();
  const topics = [
    { icon: '📊', color: '#4096ff' },
    { icon: '🧭', color: '#52c41a' },
    { icon: '🎯', color: '#fa541c' },
    { icon: '🔬', color: '#722ed1' },
    { icon: '📐', color: '#faad14' }
  ];
  
  return (
    <div style={{ 
      display: 'flex', 
      gap: isMobile ? '8px' : '12px',
      justifyContent: 'center',
      marginTop: isMobile ? '16px' : '24px'
    }}>
      {topics.map((topic, index) => (
        <div 
          key={index}
          style={{
            width: isMobile ? '48px' : '60px',
            height: isMobile ? '48px' : '60px',
            borderRadius: isMobile ? '12px' : '16px',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: isMobile ? '20px' : '24px',
            cursor: 'pointer',
            transition: 'transform 0.2s',
            border: '1px solid #f0f0f0'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          {topic.icon}
        </div>
      ))}
    </div>
  );
};

// Feedback Questions Modal Component
const FeedbackQuestionsModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  feedbackData: any;
  onSubmitSuccess?: (feedbackId: number) => void;
}> = ({ visible, onClose, feedbackData, onSubmitSuccess }) => {
  const { isMobile } = useResponsive();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  
  const { data: questions, isLoading: questionsLoading } = useFeedbackQuestions(feedbackData?.id);
  
  console.log('Feedback Questions in Modal:', questions);
  
  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const currentUser = authService.getCurrentUser();
      const userId = currentUser?.id || 1; // Fallback to 1 if no user ID
      
      // Convert form values to responses array in order of questions
      const responses: string[] = [];
      questions?.forEach((question: any) => {
        const questionKey = `question_${question.id}`;
        if (values[questionKey]) {
          responses.push(values[questionKey]);
        }
      });
      
      const responseData = {
        feedback_id: feedbackData.id,
        user_id: parseInt(userId.toString()),
        responses: responses
      };
      
      console.log('Submitting feedback response:', responseData);
      
      await submitFeedbackResponse(responseData);
      
      message.success('Feedback submitted successfully!');
      form.resetFields();
      
      // Call success callback if provided
      if (onSubmitSuccess) {
        onSubmitSuccess(feedbackData.id);
      } else {
        onClose();
      }
    } catch (error: any) {
      console.error('Failed to submit feedback:', error);
      message.error(error.message || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <Modal
      title="Class Feedback"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={isMobile ? '95%' : 600}
      centered
      closable={false}
      maskClosable={false}
      style={{ maxHeight: '90vh' }}
      bodyStyle={{ 
        maxHeight: '70vh', 
        overflowY: 'auto',
        padding: isMobile ? '16px' : '24px'
      }}
    >
      <div style={{ marginBottom: '20px' }}>
        <Typography.Title level={4} style={{ margin: 0, marginBottom: '8px' }}>
          {feedbackData?.title}
        </Typography.Title>
        <Typography.Text type="secondary">
          {feedbackData?.description}
        </Typography.Text>
      </div>
      
      {questionsLoading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {questions?.map((question: any, index: number) => (
            <Form.Item
              key={question.id}
              name={`question_${question.id}`}
              label={
                <span style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '500' }}>
                  {index + 1}. {question.question_text}
                </span>
              }
              rules={[{ required: true, message: 'Please provide an answer' }]}
              style={{ marginBottom: '24px' }}
            >
              {question.question_type === 'multiple_choice' ? (
                <Radio.Group style={{ width: '100%' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {question.options?.map((option: string, optionIndex: number) => (
                      <Radio 
                        key={optionIndex} 
                        value={option}
                        style={{ 
                          fontSize: isMobile ? '14px' : '16px',
                          padding: '8px 0'
                        }}
                      >
                        {option}
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              ) : (
                <Input.TextArea
                  rows={4}
                  placeholder="Please provide your detailed feedback..."
                  style={{
                    fontSize: isMobile ? '14px' : '16px',
                    borderRadius: '8px'
                  }}
                />
              )}
            </Form.Item>
          ))}
          
          <Form.Item style={{ marginBottom: 0, marginTop: '32px' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                size="large"
                onClick={onClose}
                style={{
                  borderRadius: '8px',
                  minWidth: '100px'
                }}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={submitting}
                style={{
                  borderRadius: '8px',
                  minWidth: '120px'
                }}
              >
                Submit Feedback
              </Button>
            </Space>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

// Class Feedback Component
const ClassFeedbackSection: React.FC = () => {
  const { isMobile } = useResponsive();
  const currentUser = authService.getCurrentUser();
  const studentId = currentUser?.id;
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [submittedFeedbackIds, setSubmittedFeedbackIds] = useState<number[]>([]);
  
  // Use hooks
  const { data: studentCheck, isLoading: checkLoading, error: checkError } = useStudentCheck(String(studentId));
  const ClassId = studentCheck?.id ? String(studentCheck.id) : undefined;
  const CourseId = studentCheck?.course_id;
  const { data: classFeedback, isLoading: feedbackLoading, error: feedbackError } = useClassFeedback(
    ClassId, 
    currentUser?.id ? String(currentUser.id) : undefined
  );
  
  // Debug logging
  console.log('Class Feedback Data:', classFeedback);
  console.log('Student Check Data:', studentCheck);
  
  // Handle feedback data (it might be an array or single object)
  const feedbackArray = Array.isArray(classFeedback) ? classFeedback : classFeedback ? [classFeedback] : [];
  const activeFeedback = feedbackArray.length > 0 
    ? (feedbackArray.find(feedback => feedback.is_active === 1) || feedbackArray[0]) 
    : null;
  
  // Check if user has already submitted feedback for the active feedback
  const { data: hasSubmittedFeedback, isLoading: submissionCheckLoading } = useUserSubmittedFeedback(
    studentId || '',
    activeFeedback?.id || ''
  );
  
  console.log('Has submitted feedback:', hasSubmittedFeedback);
  console.log('Submission check loading:', submissionCheckLoading);
  console.log('Feedback array length:', feedbackArray.length);
  
  // Auto-show modal when feedback is available and not yet submitted
  useEffect(() => {
    if (
      activeFeedback && 
      !feedbackLoading && 
      !submissionCheckLoading &&
      !showFeedbackModal && 
      !submittedFeedbackIds.includes(activeFeedback.id) &&
      !hasSubmittedFeedback
    ) {
      setShowFeedbackModal(true);
    }
  }, [activeFeedback, feedbackLoading, submissionCheckLoading, showFeedbackModal, submittedFeedbackIds, hasSubmittedFeedback]);
  
  if (feedbackLoading || checkLoading || submissionCheckLoading) {
    return (
      <Card 
        style={{ 
          borderRadius: '16px',
          border: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          minHeight: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Spin size="large" />
      </Card>
    );
  }
  
  if (feedbackError || checkError) {
    console.error('Feedback error:', feedbackError);
    console.error('Student check error:', checkError);
  }
  
  return (
    <Card 
      style={{ 
        borderRadius: '16px',
        border: 'none',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}
    >
      <div style={{ padding: isMobile ? '16px' : '20px' }}>
        {/* Student Check Section - Currently hidden, kept for future use */}
        {/* {studentCheck && (
          <div style={{ marginBottom: isMobile ? '16px' : '20px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: isMobile ? '8px' : '12px'
            }}>
              <div style={{
                width: isMobile ? '32px' : '40px',
                height: isMobile ? '32px' : '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: isMobile ? '8px' : '12px',
                fontSize: isMobile ? '16px' : '20px'
              }}>
                📚
              </div>
              <div>
                <Title level={5} style={{ margin: 0, fontSize: isMobile ? '14px' : '16px' }}>
                  {studentCheck.class_title}
                </Title>
                <Text style={{ 
                  color: '#8c8c8c', 
                  fontSize: isMobile ? '11px' : '12px' 
                }}>
                  Course ID: {studentCheck.course_id}
                </Text>
              </div>
            </div>
          </div>
        )} */}
        
        {/* Class Feedback Section */}
        {activeFeedback && feedbackArray.length > 0 && (
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: isMobile ? '8px' : '12px'
            }}>
              <div style={{
                width: isMobile ? '32px' : '40px',
                height: isMobile ? '32px' : '40px',
                borderRadius: '50%',
                background: activeFeedback.is_active === 1 
                  ? 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)'
                  : 'linear-gradient(135deg, #faad14 0%, #ffc53d 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: isMobile ? '8px' : '12px',
                fontSize: isMobile ? '16px' : '20px'
              }}>
                {activeFeedback.is_active === 1 ? '✅' : '💬'}
              </div>
              <div style={{ flex: 1 }}>
                <Title level={5} style={{ 
                  margin: 0, 
                  fontSize: isMobile ? '14px' : '16px',
                  lineHeight: '1.4'
                }}>
                  {activeFeedback.title}
                </Title>
                <Text style={{ 
                  color: '#8c8c8c', 
                  fontSize: isMobile ? '11px' : '12px' 
                }}>
                  Class ID: {activeFeedback.class_id} • {new Date(activeFeedback.created_at).toLocaleDateString()}
                </Text>
              </div>
            </div>
            
            <Text style={{ 
              color: '#595959',
              fontSize: isMobile ? '12px' : '14px',
              lineHeight: '1.5',
              display: 'block',
              marginTop: isMobile ? '8px' : '12px'
            }}>
              {activeFeedback.description}
            </Text>
            
            <div style={{
              marginTop: isMobile ? '12px' : '16px',
              padding: isMobile ? '8px 12px' : '10px 16px',
              backgroundColor: (hasSubmittedFeedback || submittedFeedbackIds.includes(activeFeedback.id))
                ? '#f6ffed' 
                : (activeFeedback.is_active === 1 ? '#fff7e6' : '#fffbe6'),
              borderRadius: '8px',
              border: `1px solid ${(hasSubmittedFeedback || submittedFeedbackIds.includes(activeFeedback.id))
                ? '#b7eb8f' 
                : (activeFeedback.is_active === 1 ? '#ffd591' : '#ffe58f')}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Text style={{ 
                fontSize: isMobile ? '11px' : '12px',
                color: (hasSubmittedFeedback || submittedFeedbackIds.includes(activeFeedback.id))
                  ? '#389e0d' 
                  : (activeFeedback.is_active === 1 ? '#d46b08' : '#d48806'),
                fontWeight: '500'
              }}>
                Status: {(hasSubmittedFeedback || submittedFeedbackIds.includes(activeFeedback.id))
                  ? 'Feedback Submitted ✓' 
                  : (activeFeedback.is_active === 1 ? 'Pending Feedback' : 'Inactive')}
              </Text>
              
              {activeFeedback.is_active === 1 && 
               !hasSubmittedFeedback && 
               !submittedFeedbackIds.includes(activeFeedback.id) && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => setShowFeedbackModal(true)}
                  style={{
                    fontSize: isMobile ? '11px' : '12px',
                    color: '#d46b08',
                    padding: '0 4px',
                    height: 'auto'
                  }}
                >
                  Give Feedback
                </Button>
              )}
            </div>
          </div>
        )}
        
        {/* Fallback when no data */}
        {(!activeFeedback || feedbackArray.length === 0) && !feedbackLoading && !checkLoading && (
          <div style={{ textAlign: 'center', padding: isMobile ? '20px' : '32px' }}>
            <div style={{ fontSize: isMobile ? '32px' : '48px', marginBottom: '12px' }}>
              📋
            </div>
            <Title level={5} style={{ color: '#8c8c8c', margin: 0 }}>
              No class feedback available
            </Title>
            <Text style={{ color: '#bfbfbf', fontSize: isMobile ? '12px' : '14px' }}>
              Check back later for updates
            </Text>
          </div>
        )}
      </div>
      
      {/* Feedback Questions Modal */}
      <FeedbackQuestionsModal
        visible={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        feedbackData={activeFeedback}
        onSubmitSuccess={(feedbackId: number) => {
          setSubmittedFeedbackIds(prev => [...prev, feedbackId]);
          setShowFeedbackModal(false);
        }}
      />
    </Card>
  );
};

const Dashboard: React.FC = () => {
  const { isMobile } = useResponsive();
  useSecurityProtection();


  return (
    <div style={{ 
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '16px' : '24px'
    }}>
      {/* <FacebookLinkModal /> */}
      <Row gutter={[24, 24]}>
        {/* Left Column */}
        <Col xs={24} lg={12}>
          <Space direction="vertical" size={isMobile ? 16 : 24} style={{ width: '100%' }}>
            {/* <PremiumUnlock /> */}
            <Achievement />
            <ClassFeedbackSection />
          </Space>
        </Col>
        
        {/* Right Column */}
        <Col xs={24} lg={12}>
          <NumberTheoryCourse />
          {/* <CourseTopics /> */}
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;